// The four things the assistant can do inside a visit, and what each costs.
// core.md §7.5 (دستیار), §8.3.
//
//   ask                 askInEncounter             10 پارسه
//   differential        encounterDifferential      10 پارسه
//   suggestSoap         suggestEncounterSoap       10 پارسه
//   suggestOrders       suggestEncounterOrders     10 پارسه
//   checkInteractions   checkEncounterInteractions  3 پارسه
//
// ── Every one of them is a MUTATION, and that is not an accident ──────────
//
// They spend money and they write two messages into the visit's copilot thread.
// A money-spending Query would be re-issued by any refetch, any window focus and
// any cache revalidation — each time at the student's expense. Nothing in this
// hook may be called from a render path.
//
// ── The cost gate is a COURTESY; the server is the gate ───────────────────
//
// `canAfford` compares the price list against the balance the identity store
// already holds and saves a round trip that could only fail. It is deliberately
// permissive: a balance that has not loaded ALLOWS the call, because the AI
// service's own credit check is the authority and it fails OPEN when the wallet
// is unreachable (core.md §3.5). When either gate says no, the recovery is
// «شارژ پارسه» — never a retry, which cannot succeed because the balance has
// not moved.
//
// And the refusal touches nothing else: an out-of-پارسه student keeps a fully
// usable note, diagnosis list, prescription and order list (§7.5). Only the AI
// buttons change what they do.
//
// ── Results are HELD, not applied ────────────────────────────────────────
//
// Each operation parks its answer in state — the differential list, the SOAP
// draft, the order suggestions, the interaction warnings — and the screen renders
// a review surface over it. Nothing here writes a clinical row; the student's
// click does, through `useEncounter` (core.md §3.4).
//
// ── And they SURVIVE a reload, because the BFF wrote them down ───────────
//
// Every answer is also two messages on the visit's copilot thread (core.md
// §7.5, §8.3): the student's turn and the assistant's, the latter carrying the
// parsed answer as `payload`. So the tabs are hydrated from that thread on
// mount — the last `differential` message is the افتراقی tab, the last
// `soap_suggest` / `order_suggest` are the پیشنهادها tab, the `chat` turns are
// the پرسش transcript — and an answer a student paid for is still there after
// an F5. The thread is READ here, never created: it exists only once the first
// answer has been written (the BFF creates it after the AI call, not before).
//
// A mutation that lands before the hydration query answers wins: hydration
// only fills what is still empty.

import React from 'react';

import { bffQuery, bffMutation, errorCode, isOutOfCredits } from '../lib/bffClient.js';
import {
  ASK_IN_ENCOUNTER_MUTATION,
  ENCOUNTER_DIFFERENTIAL_MUTATION,
  SUGGEST_ENCOUNTER_SOAP_MUTATION,
  SUGGEST_ENCOUNTER_ORDERS_MUTATION,
  CHECK_ENCOUNTER_INTERACTIONS_MUTATION,
  CASE_CHAT_THREADS_QUERY,
  CASE_CHAT_THREAD_QUERY,
} from '../graphql/documents.js';
import { canAfford } from '../constants/pricing.js';
import { costOf, useCreditPrices } from '../lib/useCreditPrices.js';
import { refreshBalance } from './useMe.js';

/**
 * The operations, their documents and their prices — one table, read twice.
 *
 * `cost` is a GETTER, not a number: it resolves through `costOf(action)` at the
 * moment it is read, so the courtesy gate and the chips follow the active
 * pricing policy once the price list has arrived, and the shipped constant
 * until then. `action` is the PARSEH_COSTS key the op is priced as.
 */
const op = (doc, action, field) => Object.freeze({
  doc, action, field, get cost() { return costOf(action); },
});

export const COPILOT_OPS = Object.freeze({
  ask: op(ASK_IN_ENCOUNTER_MUTATION, 'ASK', 'askInEncounter'),
  differential: op(ENCOUNTER_DIFFERENTIAL_MUTATION, 'DIFFERENTIAL', 'encounterDifferential'),
  soap: op(SUGGEST_ENCOUNTER_SOAP_MUTATION, 'SOAP_SUGGEST', 'suggestEncounterSoap'),
  orders: op(SUGGEST_ENCOUNTER_ORDERS_MUTATION, 'ORDER_SUGGEST', 'suggestEncounterOrders'),
  interactions: op(CHECK_ENCOUNTER_INTERACTIONS_MUTATION, 'INTERACTION', 'checkEncounterInteractions'),
});

/** The messages a copilot answer carries back, oldest first. */
const messagesOf = (payload) => payload?.thread?.messages || [];

/** The `payload` of a stored message — a JSON scalar, which some transports hand over as text. */
const payloadOf = (message) => {
  const raw = message?.payload;
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return typeof raw === 'object' ? raw : null;
};

/** The last assistant message of one kind on a thread, or null. */
const lastAssistantOf = (messages, kind) => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.role === 'assistant' && m?.kind === kind) return m;
  }
  return null;
};

/**
 * What the tabs are hydrated with from a thread's messages. Pure, so the
 * mapping from what the BFF stores (`writeExchange` in its ai resolvers) to
 * what the panels render can be pinned by a test without a network:
 *
 *   differential   payload { narrative, items }             → { narrative, items }
 *   soap_suggest   payload { narrative, soap: {s,o,a,p} }   → { narrative, s, o, a, p }
 *   order_suggest  payload { narrative, items }             → { narrative, items }
 *   interaction    payload { hasInteractions, warnings, skippedDrugNames }
 *   chat           the turns themselves, in order
 */
export function hydrateFromThread(messages = []) {
  const rows = Array.isArray(messages) ? messages : [];
  const chat = rows.filter((m) => m?.kind === 'chat' && m?.content);

  const dx = lastAssistantOf(rows, 'differential');
  const dxPayload = payloadOf(dx);
  const differential = dx
    ? { narrative: dxPayload?.narrative ?? dx.content ?? '', items: dxPayload?.items || [] }
    : null;

  const soap = lastAssistantOf(rows, 'soap_suggest');
  const soapPayload = payloadOf(soap);
  const soapSuggestion = soap
    ? {
      narrative: soapPayload?.narrative ?? soap.content ?? '',
      s: soapPayload?.soap?.s ?? null,
      o: soapPayload?.soap?.o ?? null,
      a: soapPayload?.soap?.a ?? null,
      p: soapPayload?.soap?.p ?? null,
    }
    : null;

  const orders = lastAssistantOf(rows, 'order_suggest');
  const ordersPayload = payloadOf(orders);
  const orderSuggestion = orders
    ? { narrative: ordersPayload?.narrative ?? orders.content ?? '', items: ordersPayload?.items || [] }
    : null;

  const ix = lastAssistantOf(rows, 'interaction');
  const ixPayload = payloadOf(ix);
  const interactions = ix && ixPayload
    ? {
      hasInteractions: !!ixPayload.hasInteractions,
      warnings: ixPayload.warnings || [],
      skippedDrugNames: ixPayload.skippedDrugNames || [],
    }
    : null;

  return { chat, differential, soapSuggestion, orderSuggestion, interactions };
}

/**
 * @param opts.balance    the identity store's balance — the courtesy gate reads it
 * @param opts.caseId     the visit's patient — the thread list is keyed by it
 * @param opts.onRefusal  `({ reason: 'credits' | 'error', error, op })`, called
 *                        whenever an operation is refused, locally or by the
 *                        server, so the screen can say so where the button was
 *                        pressed even when the copilot panel is not on screen
 */
export function useCopilot(encounterId, { balance = null, caseId = null, onRefusal } = {}) {
  // Subscribes this hook's caller to the price list, so `costs` follows the policy.
  const prices = useCreditPrices();
  const [busy, setBusy] = React.useState(null);      // which op is in flight
  const [error, setError] = React.useState(null);
  const [needsTopUp, setNeedsTopUp] = React.useState(false);

  // The visit's copilot transcript, accumulated from what each answer returns.
  // The BFF answers with ONLY the two new messages, so this appends rather than
  // re-reads — and a re-read would be a second round trip for text already here.
  const [messages, setMessages] = React.useState([]);
  const [followUps, setFollowUps] = React.useState([]);
  const [threadId, setThreadId] = React.useState(null);

  const [differential, setDifferential] = React.useState(null);
  const [soapSuggestion, setSoapSuggestion] = React.useState(null);
  const [orderSuggestion, setOrderSuggestion] = React.useState(null);
  const [interactions, setInteractions] = React.useState(null);

  const clearError = React.useCallback(() => { setError(null); setNeedsTopUp(false); }, []);

  const refusalRef = React.useRef(onRefusal);
  refusalRef.current = onRefusal;

  // ── Hydration from the stored thread ────────────────────────────────────
  React.useEffect(() => {
    if (!encounterId || !caseId) return undefined;
    let alive = true;
    (async () => {
      try {
        const listed = await bffQuery(CASE_CHAT_THREADS_QUERY, {
          caseId, scope: 'ENCOUNTER', encounterId,
        });
        const row = (listed?.caseChatThreads || [])[0];
        if (!row || !alive) return;
        const full = await bffQuery(CASE_CHAT_THREAD_QUERY, { threadId: row.id });
        const thread = full?.caseChatThread;
        if (!thread || !alive) return;
        const got = hydrateFromThread(thread.messages || []);
        setThreadId((prev) => prev || thread.id || null);
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          return [...got.chat.filter((m) => !seen.has(m.id)), ...prev];
        });
        setDifferential((prev) => prev || got.differential);
        setSoapSuggestion((prev) => prev || got.soapSuggestion);
        setOrderSuggestion((prev) => prev || got.orderSuggestion);
        setInteractions((prev) => prev || got.interactions);
      } catch (err) {
        // A transcript that could not be read is an empty transcript, not a
        // broken workspace — the next answer still appends normally.
        console.warn('[useCopilot] thread hydration failed:', errorCode(err) || err?.message);
      }
    })();
    return () => { alive = false; };
  }, [encounterId, caseId]);

  const run = React.useCallback(async (op, vars = {}) => {
    const spec = COPILOT_OPS[op];
    if (!spec || !encounterId) return null;
    setError(null);
    // The courtesy gate. Refusing here spends nothing and shows the same
    // recovery the server's refusal shows.
    if (!canAfford(balance, spec.cost)) {
      setNeedsTopUp(true);
      refusalRef.current?.({ reason: 'credits', error: null, op });
      return null;
    }
    setNeedsTopUp(false);
    setBusy(op);
    try {
      const data = await bffMutation(spec.doc, { encounterId, ...vars });
      const payload = data?.[spec.field] || null;
      if (payload?.thread) {
        setThreadId(payload.thread.id || null);
        const incoming = messagesOf(payload);
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          return [...prev, ...incoming.filter((m) => !seen.has(m.id))];
        });
      }
      // The money moved; the rail's balance must not keep saying otherwise.
      refreshBalance();
      return payload;
    } catch (err) {
      console.warn(`[useCopilot] ${op} failed:`, errorCode(err) || err?.message);
      if (isOutOfCredits(err)) {
        setNeedsTopUp(true);
        refusalRef.current?.({ reason: 'credits', error: err, op });
      } else {
        setError(err);
        refusalRef.current?.({ reason: 'error', error: err, op });
      }
      return null;
    } finally {
      setBusy(null);
    }
  }, [encounterId, balance]);

  /** پرسش — a free question about THIS visit. The student's turn costs nothing. */
  const ask = React.useCallback(async (question) => {
    const text = String(question || '').trim();
    if (!text) return null;
    const payload = await run('ask', { question: text });
    setFollowUps(payload?.followUpSuggestions || []);
    return payload;
  }, [run]);

  const runDifferential = React.useCallback(async () => {
    const payload = await run('differential');
    if (payload) setDifferential(payload);
    return payload;
  }, [run]);

  const suggestSoap = React.useCallback(async () => {
    const payload = await run('soap');
    if (payload) setSoapSuggestion(payload);
    return payload;
  }, [run]);

  const suggestOrders = React.useCallback(async () => {
    const payload = await run('orders');
    if (payload) setOrderSuggestion(payload);
    return payload;
  }, [run]);

  const checkInteractions = React.useCallback(async () => {
    const payload = await run('interactions');
    if (payload) setInteractions(payload);
    return payload;
  }, [run]);

  return {
    busy,
    error,
    clearError,
    needsTopUp,
    // Re-read on every render, so the chips follow the policy when it lands.
    costs: {
      ask: prices.costOf('ASK'),
      differential: prices.costOf('DIFFERENTIAL'),
      soap: prices.costOf('SOAP_SUGGEST'),
      orders: prices.costOf('ORDER_SUGGEST'),
      interactions: prices.costOf('INTERACTION'),
    },

    messages,
    followUps,
    threadId,

    ask,
    differential,
    runDifferential,
    soapSuggestion,
    setSoapSuggestion,
    suggestSoap,
    orderSuggestion,
    setOrderSuggestion,
    suggestOrders,
    interactions,
    setInteractions,
    checkInteractions,
  };
}

export default useCopilot;
